import type { AiAnalysisState, SuggestedAction } from '@/store/slices/aiAnalysisTypes';
import { MAX_VISIBLE_NON_QUESTIONS, MAX_VISIBLE_QUESTIONS, MIN_VISIBLE_TASK_SLOTS } from '@/lib/actionDisplayCaps';
import { RECORDING_CONSENT_TASK_ID } from '@/lib/recordingConsent';
import { wrapUpActionRank } from '@/lib/wrapUp';

export type CanonicalLifecycleState =
  | 'active'
  | 'dismissed'
  | 'removed'
  | 'resolved'
  | 'discarded'
  | 'follow_up'
  | 'purged'
  // #1867: an AI suggestion the AI withdrew. Out of the live lane, still on the record (review + disclosure).
  | 'withdrawn';

export type CanonicalDisplayState = 'visible' | 'queued' | 'hidden' | 'archived';
export type CanonicalAnswerStatus = 'unanswered' | 'partial' | 'complete';

export interface CanonicalActionRow {
  action_instance_id?: string;
  call_id?: string;
  action_key: string;
  source_action_id?: string | null;
  action_type?: string | null;
  text?: string | null;
  details?: string | null;
  reason?: string | null;
  priority?: number | string | null;
  required?: boolean | null;
  source?: string | null;
  lifecycle_state?: CanonicalLifecycleState | string | null;
  display_state?: CanonicalDisplayState | string | null;
  answer_status?: CanonicalAnswerStatus | string | null;
  answer?: string | null;
  partial_answer?: string | null;
  answer_metadata?: Record<string, unknown> | null;
  officer_addressed?: boolean | null;
  officer_addressed_at?: string | null;
  officer_addressed_partial?: boolean | null;
  officer_quote?: string | null;
  officer_addressed_reason?: string | null;
  dedup_metadata?: Record<string, unknown> | null;
  conflict_metadata?: Record<string, unknown> | null;
  deception_metadata?: Record<string, unknown> | null;
  source_metadata?: Record<string, unknown> | null;
  raw_payload?: Record<string, unknown> | null;
  note?: string | null;
  order_index?: number | null;
  dismissed_at?: string | null;
  removed_at?: string | null;
  restored_at?: string | null;
  resolved_at?: string | null;
  follow_up_at?: string | null;
  withdrawn_at?: string | null;
  spoken_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  revision?: number | string | null;
}

export interface CanonicalSpeechQueueRow {
  id?: string;
  action_instance_id?: string | null;
  action_key: string;
  action_text?: string | null;
  queue_origin?: 'officer' | 'auto' | string | null;
  source?: 'manual' | 'auto-response' | string | null;
  status?: 'queued' | 'in_flight' | 'completed' | 'failed' | 'cancelled' | string | null;
  attempt_id?: string | null;
  position?: number | null;
  claimed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CanonicalSpeechAttemptRow {
  action_key: string;
  attempt_id: string;
  source?: 'manual' | 'auto-response' | string | null;
  status?: 'queued' | 'speaking' | 'completed' | 'failed' | 'cancelled' | string | null;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  failed_at?: string | null;
  failure_reason?: string | null;
}

export interface CanonicalActionGroupProjection {
  id: string;
  label: string;
  type: string;
  metadata: Record<string, unknown>;
  actionIds: string[];
}

export interface CanonicalProjection extends Partial<AiAnalysisState> {
  suggestedActions: SuggestedAction[];
  queuedActions: SuggestedAction[];
  allSuggestedActions: SuggestedAction[];
  resolvedSuggestions: SuggestedAction[];
  discardedSuggestions: SuggestedAction[];
  removedSuggestions: SuggestedAction[];
  partialSuggestions: SuggestedAction[];
  answeredPrompts: string[];
  partialPrompts: string[];
  actionGroups: CanonicalActionGroupProjection[];
  speechQueue: Array<Record<string, unknown>>;
  canonicalRevision: number;
  /**
   * Agent-managed queue Phase 5: the TRANSACTIONALLY-monotonic
   * call_speech_queue_state.canonical_revision, stamped server-side on
   * normalized-authority calls. Unlike the row-derived `canonicalRevision`
   * max (timestamp-scale — it can tie within a tick or go BACKWARD when the
   * newest row is deleted), this counter strictly increases per canonical
   * mutation, so the client gate can never suppress a newer projection.
   * Absent on legacy calls / pre-migration DBs — the gate falls back to the
   * row-derived comparison. The two scales are NEVER cross-compared.
   */
  canonicalStateRevision?: number;
}

export interface CanonicalActionUpsert {
  action_key: string;
  source_action_id: string | null;
  action_type: string;
  text: string;
  details: string;
  reason: string | null;
  priority: number | null;
  required: boolean;
  source: string;
  lifecycle_state: CanonicalLifecycleState;
  display_state: CanonicalDisplayState;
  answer_status: CanonicalAnswerStatus;
  answer: string | null;
  partial_answer: string | null;
  answer_metadata: Record<string, unknown>;
  officer_addressed: boolean;
  officer_addressed_at: string | null;
  officer_addressed_partial: boolean;
  officer_quote: string | null;
  officer_addressed_reason: string | null;
  dedup_metadata: Record<string, unknown>;
  conflict_metadata: Record<string, unknown>;
  deception_metadata: Record<string, unknown>;
  source_metadata: Record<string, unknown>;
  raw_payload: Record<string, unknown>;
  note: string | null;
  order_index: number;
  spoken_at: string | null;
}

const TERMINAL_LIFECYCLE_STATES = new Set<CanonicalLifecycleState>([
  'dismissed',
  'removed',
  'resolved',
  'discarded',
  'follow_up',
  'purged',
  'withdrawn',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

export function cleanString(value: unknown, maxLength?: number): string {
  if (typeof value !== 'string') return '';
  // Content has no length budget. Explicit limits belong to identifier/chrome
  // callers; preserving long answers also preserves their line breaks.
  if (maxLength === undefined) return value.trim();
  return value.replace(/[\0-\x1F\x7F]/g, '').trim().slice(0, maxLength);
}

function optionalString(value: unknown, maxLength?: number): string | null {
  const cleaned = cleanString(value, maxLength);
  return cleaned ? cleaned : null;
}

function parseNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function parseBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  return false;
}

function parseIso(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
}

function parseTimestampMicros(value: unknown): number {
  if (typeof value !== 'string' || !value.trim()) return 0;
  const text = value.trim();
  const timestampMs = Date.parse(text);
  if (!Number.isFinite(timestampMs)) return 0;

  const fractional = text.match(/\.(\d{1,6})/)?.[1];
  if (!fractional) return Math.floor(timestampMs) * 1000;

  const microsWithinSecond = Number(fractional.padEnd(6, '0').slice(0, 6));
  return Math.floor(timestampMs / 1000) * 1_000_000 + microsWithinSecond;
}

function rowProjectionRevision(row: CanonicalActionRow): number {
  return Math.max(
    parseTimestampMicros(row.updated_at),
    parseTimestampMicros(row.created_at),
    parseNumber(row.revision) ?? 0,
  );
}

function speechQueueProjectionRevision(row: CanonicalSpeechQueueRow): number {
  return Math.max(
    parseTimestampMicros(row.updated_at),
    parseTimestampMicros(row.created_at),
    parseTimestampMicros(row.claimed_at),
  );
}

function speechAttemptProjectionRevision(row: CanonicalSpeechAttemptRow): number {
  return Math.max(
    parseTimestampMicros(row.queued_at),
    parseTimestampMicros(row.started_at),
    parseTimestampMicros(row.completed_at),
    parseTimestampMicros(row.cancelled_at),
    parseTimestampMicros(row.failed_at),
  );
}

function stableHash(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

export function getLegacyActionId(row: Pick<CanonicalActionRow, 'source_action_id' | 'action_key'>): string {
  return cleanString(row.source_action_id, 200) || cleanString(row.action_key, 200);
}

/**
 * Phase-1 latency instrumentation. Maps the rows a canonical ingest ACTUALLY inserted (the
 * upsert's `... ON CONFLICT DO NOTHING RETURNING` result — see server callActions.ts
 * `ingestActions`) to their projection-space ids, so the agent-backend probe can attribute
 * newly-visible actions to the exact generation trigger. A row the DB did NOT return — e.g. a
 * concurrent-conflict loser — is simply absent from the input, and therefore from the ids.
 */
export function acceptedIdsOf(
  rows: ReadonlyArray<Pick<CanonicalActionRow, 'source_action_id' | 'action_key'>>,
): string[] {
  return rows.map((row) => getLegacyActionId(row));
}

export function makeActionKey(raw: unknown): string {
  const record = isRecord(raw) ? raw : {};
  const explicit =
    cleanString(record.actionKey, 200) ||
    cleanString(record.action_key, 200) ||
    cleanString(record.id, 200) ||
    cleanString(record.source_action_id, 200);
  if (explicit) return explicit;

  const type = cleanString(record.type || record.action_type, 80) || 'suggestion';
  // Existing canonical rows use this legacy hash input. Keep their identity
  // stable; normalized/storage/display content below remains complete.
  const text =
    cleanString(record.details, 8000) ||
    cleanString(record.description, 8000) ||
    cleanString(record.text, 8000) ||
    JSON.stringify(record);
  return `generated:${stableHash(`${type}:${text}`)}`;
}

function normalizeActionType(value: unknown): SuggestedAction['type'] {
  const type = cleanString(value, 80);
  if (
    type === 'question' ||
    type === 'task' ||
    type === 'suggestion' ||
    type === 'conflicting_statement' ||
    type === 'deception_detection' ||
    type === 'revisit_thread'
  ) {
    return type;
  }
  return 'suggestion';
}

function normalizeLifecycle(value: unknown, fallback: CanonicalLifecycleState): CanonicalLifecycleState {
  const state = cleanString(value, 40) as CanonicalLifecycleState;
  return TERMINAL_LIFECYCLE_STATES.has(state) || state === 'active' ? state : fallback;
}

function normalizeDisplay(value: unknown, fallback: CanonicalDisplayState): CanonicalDisplayState {
  const display = cleanString(value, 40);
  if (display === 'visible' || display === 'queued' || display === 'hidden' || display === 'archived') {
    return display;
  }
  return fallback;
}

function normalizeAnswerStatus(value: unknown, raw: Record<string, unknown>): CanonicalAnswerStatus {
  const status = cleanString(value, 40);
  if (status === 'partial' || status === 'complete') return status;
  if (parseBoolean(raw.answered)) return 'complete';
  return 'unanswered';
}

function recordFromMetadata(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function pickMetadata(raw: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) out[key] = raw[key];
  }
  return out;
}

function pickSourceMetadata(raw: Record<string, unknown>): Record<string, unknown> {
  return {
    ...recordFromMetadata(raw.source_metadata),
    ...recordFromMetadata(raw.sourceMetadata),
    ...pickMetadata(raw, [
      'entityIds',
      'entityType',
      'sourceEntityId',
      'sourceEntityName',
      'sources',
      'threadNodeId',
      'threadCategory',
      'relevanceScore',
      'valuePotential',
      'relevanceReasoning',
      'suggestedFollowUp',
      'triggeredBy',
      'triggerQuote',
      'triggerTimestampMs',
      'suggestionAudience',
      'pipelineOrigin',
      'pipelineMode',
      'pipelineRunId',
      'frameId',
      'frameSignature',
      'generatorLane',
      'writeOwner',
      'comparisonGroupId',
      'experimental',
    ]),
  };
}

export function normalizeLegacyActionForCanonical(
  raw: unknown,
  options: {
    orderIndex?: number;
    lifecycleState?: CanonicalLifecycleState;
    displayState?: CanonicalDisplayState;
    source?: string;
  } = {},
): CanonicalActionUpsert | null {
  if (!isRecord(raw)) return null;

  const actionKey = makeActionKey(raw);
  const details = cleanString(raw.details) || cleanString(raw.description) || cleanString(raw.text);
  const text = cleanString(raw.text) || details;
  if (!actionKey || !details) return null;

  const answerStatus = normalizeAnswerStatus(raw.answerStatus ?? raw.answer_status, raw);
  const actionType = normalizeActionType(raw.type ?? raw.action_type);

  return {
    action_key: actionKey,
    source_action_id: optionalString(raw.id ?? raw.source_action_id, 200),
    action_type: actionType,
    text,
    details,
    reason: optionalString(raw.reason),
    priority: parseNumber(raw.priority),
    required: parseBoolean(raw.required),
    source: cleanString(raw.source, 80) || options.source || 'ai',
    lifecycle_state: normalizeLifecycle(raw.lifecycle_state, options.lifecycleState || 'active'),
    display_state: normalizeDisplay(raw.display_state, options.displayState || 'visible'),
    answer_status: answerStatus,
    answer: optionalString(raw.answer),
    partial_answer: optionalString(raw.partialAnswer ?? raw.partial_answer),
    answer_metadata: pickMetadata(raw, [
      'tierMatched',
      'confidence',
      'answerReason',
      'answerReasoning',
      'highlightedQuestion',
      'questionParts',
      'answeredParts',
      'unansweredParts',
      'completion_criteria',
      'improvementPatch',
      'answerSource',
    ]),
    officer_addressed: parseBoolean(raw.officerAddressed ?? raw.officer_addressed),
    officer_addressed_at: parseIso(raw.officerAddressedAt ?? raw.officer_addressed_at),
    officer_addressed_partial: parseBoolean(
      raw.officerAddressedPartial ?? raw.officer_addressed_partial,
    ),
    officer_quote: optionalString(raw.officerQuote ?? raw.officer_quote),
    officer_addressed_reason: optionalString(
      raw.officerAddressedReason ?? raw.officer_addressed_reason,
    ),
    dedup_metadata: pickMetadata(raw, [
      'dedupDecision',
      'dedupReason',
      'noveltyScore',
      'similarityScore',
      'closestMatchId',
      'dedupCandidates',
    ]),
    conflict_metadata: pickMetadata(raw, ['conflict_details', 'confidence_score']),
    deception_metadata: pickMetadata(raw, ['deception_indicators']),
    source_metadata: pickSourceMetadata(raw),
    raw_payload: raw,
    note: optionalString(raw.note),
    order_index:
      typeof options.orderIndex === 'number' && Number.isFinite(options.orderIndex)
        ? options.orderIndex
        : 0,
    spoken_at: parseIso(raw.spokenAt ?? raw.spoken_at),
  };
}

/**
 * The wire `description` is a legacy alias of the card text, and it is the field the agent side reads first
 * (answer detection, the speak payload, the turn-reference runtime, the generator's existing-action index, the
 * dedup anchors). A MERGE rewrites call_actions.text / details (and, for a type-priority supersession,
 * action_type) in the database but never raw_payload, so `...raw` used to leave the superseded raw sentence on
 * the wire next to the canonical one: the officer's board, the answer highlights and the speech were built from
 * one sentence while the dedup judge compared another (Q-1119 AIQ: 88 of the 783 rows of takes 9-24, among them
 * the "where IN the lot" / "where WITHIN the lot" pair of take 24).
 *
 * The canonical columns are the single source of the text. A raw description is overridden only when the
 * payload had one, so a row that never had a description keeps its shape.
 */
function canonicalDescription(row: CanonicalActionRow, raw: Record<string, unknown>): unknown {
  if (!cleanString(raw.description)) return raw.description;
  return cleanString(row.text) || cleanString(row.details) || cleanString(raw.text) || raw.description;
}

export function canonicalRowToLegacyAction(
  row: CanonicalActionRow,
  attempts: CanonicalSpeechAttemptRow[] = [],
): SuggestedAction {
  const raw = recordFromMetadata(row.raw_payload);
  const answerMetadata = recordFromMetadata(row.answer_metadata);
  const dedupMetadata = recordFromMetadata(row.dedup_metadata);
  const conflictMetadata = recordFromMetadata(row.conflict_metadata);
  const deceptionMetadata = recordFromMetadata(row.deception_metadata);
  const sourceMetadata = recordFromMetadata(row.source_metadata);
  const answerStatus = row.answer_status === 'partial' || row.answer_status === 'complete'
    ? row.answer_status
    : undefined;

  const speakAttemptsFromRows = attempts.map((attempt) => ({
    id: attempt.attempt_id,
    queuedAt: attempt.queued_at || new Date(0).toISOString(),
    startedAt: attempt.started_at || undefined,
    completedAt: attempt.completed_at || attempt.failed_at || undefined,
    cancelledAt: attempt.cancelled_at || undefined,
    // Q-427: why a cancelled/failed ask did not happen, for the card's "Not asked" state.
    failureReason: attempt.failure_reason || undefined,
    status:
      attempt.status === 'speaking' ||
      attempt.status === 'completed' ||
      attempt.status === 'failed' ||
      attempt.status === 'cancelled'
        ? attempt.status
        : 'queued',
    source:
      attempt.source === 'manual' || attempt.source === 'auto-response'
        ? attempt.source
        : undefined,
  }));

  const legacy = {
    ...raw,
    ...answerMetadata,
    ...dedupMetadata,
    ...conflictMetadata,
    ...deceptionMetadata,
    ...sourceMetadata,
    source_metadata: Object.keys(sourceMetadata).length > 0 ? sourceMetadata : undefined,
    id: getLegacyActionId(row),
    actionInstanceId: row.action_instance_id,
    actionKey: row.action_key,
    type: normalizeActionType(row.action_type || raw.type),
    details: cleanString(row.details) || cleanString(row.text) || cleanString(raw.details),
    text: cleanString(row.text) || cleanString(row.details) || cleanString(raw.text),
    description: canonicalDescription(row, raw),
    reason: row.reason ?? (raw.reason as string | undefined),
    priority: parseNumber(row.priority) ?? parseNumber(raw.priority) ?? undefined,
    required: row.required === true || raw.required === true || undefined,
    source: (row.source as SuggestedAction['source']) || raw.source,
    answerStatus,
    // Q-427: the canonical answer_status is the truth. A stale raw_payload `answered: true`
    // (a completion the server refused or never stored) must not reload as answered.
    answered: answerStatus === 'complete' ? true : row.answer_status ? undefined : raw.answered,
    answeredAt: answerStatus
      ? row.resolved_at || (raw.answeredAt as string | undefined)
      : row.answer_status
        ? undefined
        : (raw.answeredAt as string | undefined),
    answer: row.answer || (raw.answer as string | undefined),
    partialAnswer: row.partial_answer || (raw.partialAnswer as string | undefined),
    officerAddressed: row.officer_addressed === true || raw.officerAddressed === true || undefined,
    officerAddressedAt: row.officer_addressed_at || (raw.officerAddressedAt as string | undefined),
    officerAddressedPartial:
      row.officer_addressed_partial === true || raw.officerAddressedPartial === true || undefined,
    officerQuote: row.officer_quote || (raw.officerQuote as string | undefined),
    officerAddressedReason:
      row.officer_addressed_reason || (raw.officerAddressedReason as string | undefined),
    spokenAt: row.spoken_at || (raw.spokenAt as string | undefined),
    speakAttempts:
      speakAttemptsFromRows.length > 0
        ? speakAttemptsFromRows
        : Array.isArray(raw.speakAttempts)
          ? raw.speakAttempts
          : undefined,
    note: row.note || (raw.note as string | undefined),
    // #1867: a withdrawn suggestion is marked on its card; a restored one is live again and carries no mark.
    withdrawn: row.lifecycle_state === 'withdrawn' ? true : undefined,
    withdrawnAt: row.lifecycle_state === 'withdrawn' ? row.withdrawn_at || undefined : undefined,
    canonicalRevision: parseNumber(row.revision) ?? 0,
  } as SuggestedAction & {
    actionInstanceId?: string;
    actionKey?: string;
    canonicalRevision?: number;
  };

  return Object.fromEntries(
    Object.entries(legacy).filter(([, value]) => value !== undefined && value !== null),
  ) as SuggestedAction;
}

/**
 * Required system rows that lead the officer's order whatever rank they carry: recording
 * consent, then the Wrap Up items in wrap-up order (lib/wrapUp.ts). A wrap-up row is
 * created with batch order_index 0 and would otherwise compete with the prioritizer's
 * ranks and with every new non-urgent row (staging homu-dogu-lase-8178, 2026-10-26).
 */
type RequiredSystemAction = { id: string; required?: boolean | null };

/** Required system identities survive ordinary text dedup and display caps. */
export function isRequiredSystemAction(action: RequiredSystemAction): boolean {
  return action.required === true && action.id.startsWith('system-');
}

function systemLeadRank(action: RequiredSystemAction): number | null {
  if (action.required !== true) return null;
  if (action.id === RECORDING_CONSENT_TASK_ID) return -1;
  return wrapUpActionRank(action.id);
}

/** Shared by the canonical projection and the live Required section, before
 * either applies its ordinary ordering or visible-card cap. */
export function compareRequiredSystemActions(
  a: RequiredSystemAction,
  b: RequiredSystemAction,
): number {
  const leadA = systemLeadRank(a);
  const leadB = systemLeadRank(b);
  if (leadA === null && leadB === null) return 0;
  if (leadA === null) return 1;
  if (leadB === null) return -1;
  return leadA - leadB;
}

function actionSort(a: CanonicalActionRow, b: CanonicalActionRow): number {
  const lead = compareRequiredSystemActions(
    { id: String(a.action_key || a.source_action_id || ''), required: a.required },
    { id: String(b.action_key || b.source_action_id || ''), required: b.required },
  );
  if (lead !== 0) return lead;
  const order = (a.order_index ?? 0) - (b.order_index ?? 0);
  if (order !== 0) return order;
  const priority = (parseNumber(a.priority) ?? 50) - (parseNumber(b.priority) ?? 50);
  if (priority !== 0) return priority;
  const created = String(a.created_at || '').localeCompare(String(b.created_at || ''));
  if (created !== 0) return created;
  // Final deterministic tie-break — mirrors the SQL display projector
  // (officer_queue_apply_display_projection) so rows sharing order_index/priority/created_at
  // (e.g. authored in one transaction) sort identically in TS and SQL.
  return String(a.action_instance_id || '').localeCompare(String(b.action_instance_id || ''));
}

function uniqStrings(ids: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

function isRequiredSystemRow(row: CanonicalActionRow): boolean {
  const key = String(row.action_key || row.source_action_id || '');
  return isRequiredSystemAction({ id: key, required: row.required });
}

/** Durable officer interaction wins every display projection; admission never changes old rows. */
export function isOfficerTouchedSuggestion(
  row: CanonicalActionRow,
  speechQueue: readonly CanonicalSpeechQueueRow[] = [],
): boolean {
  const metadata = isRecord(row.source_metadata) ? row.source_metadata : {};
  return row.officer_addressed === true || row.officer_addressed_partial === true ||
    row.answer_status === 'partial' || row.answer_status === 'complete' ||
    Boolean(row.spoken_at || row.note || row.restored_at || row.officer_quote ||
      row.officer_addressed_at || metadata.officerHold || metadata.userDeprioritized) ||
    speechQueue.some((item) => item.action_key === row.action_key &&
      (item.queue_origin === 'officer' || item.source === 'manual') &&
      (item.status === 'queued' || item.status === 'in_flight' || item.status === 'completed'));
}

/** The kinds a full-pool refresh may repaint (agent-backend action_pipeline/lane.py FULL_POOL_REFRESH_FLOWS). */
const REFRESH_KINDS = ['suggestion', 'question'] as const;
type RefreshKind = typeof REFRESH_KINDS[number];

function hasActiveRefreshOf(rows: readonly CanonicalActionRow[], kind: RefreshKind): boolean {
  return rows.some((row) => row.lifecycle_state === 'active' &&
    row.display_state !== 'hidden' && row.display_state !== 'archived' &&
    normalizeActionType(row.action_type) === kind &&
    isRecord(row.source_metadata) && row.source_metadata.fullPoolRefresh === true);
}

export function splitCanonicalRowsByDisplay(
  rows: CanonicalActionRow[],
  speechQueue: readonly CanonicalSpeechQueueRow[] = [],
  refreshDisplayPolicy = false,
): { visible: CanonicalActionRow[]; queued: CanonicalActionRow[] } {
  const activeRows = rows
    .filter((row) => row.lifecycle_state === 'active')
    .sort(actionSort);
  const visible: CanonicalActionRow[] = [];
  const queued: CanonicalActionRow[] = [];
  let questionCount = 0;
  let taskCount = 0;
  let suggestionCount = 0;

  // Count displayable active tasks up front so suggestion admission can hold
  // back reserved slots for tasks that sort later in the list.
  let remainingTasks = activeRows.filter(
    (row) =>
      row.display_state !== 'hidden' &&
      row.display_state !== 'archived' &&
      normalizeActionType(row.action_type) === 'task' &&
      !isRequiredSystemRow(row),
  ).length;

  for (const row of activeRows) {
    if (row.display_state === 'hidden' || row.display_state === 'archived') {
      continue;
    }

    const type = normalizeActionType(row.action_type);
    if (type === 'conflicting_statement' || type === 'deception_detection') {
      visible.push(row);
      continue;
    }
    // Required system actions (e.g. the recording-consent task) are
    // policy-mandated and never compete for capped slots.
    if (isRequiredSystemRow(row)) {
      visible.push(row);
      continue;
    }
    if (type === 'question') {
      if (questionCount < MAX_VISIBLE_QUESTIONS) {
        visible.push(row);
        questionCount += 1;
      } else {
        queued.push(row);
      }
      continue;
    }
    if (type === 'task') {
      remainingTasks = Math.max(0, remainingTasks - 1);
      if (taskCount + suggestionCount < MAX_VISIBLE_NON_QUESTIONS) {
        visible.push(row);
        taskCount += 1;
      } else {
        queued.push(row);
      }
      continue;
    }
    if (type === 'suggestion') {
      const reservedForTasks = Math.max(
        0,
        Math.min(remainingTasks, MIN_VISIBLE_TASK_SLOTS - taskCount),
      );
      if (taskCount + suggestionCount + reservedForTasks < MAX_VISIBLE_NON_QUESTIONS) {
        visible.push(row);
        suggestionCount += 1;
      } else {
        queued.push(row);
      }
      continue;
    }
    visible.push(row);
  }

  // A flag alone cannot reselect pre-existing overflow. Only a card admitted
  // by a full-pool refresh activates the newest-first view, for its own kind.
  const refreshed = REFRESH_KINDS.filter((kind) => hasActiveRefreshOf(activeRows, kind));
  if (!refreshDisplayPolicy || refreshed.length === 0) return { visible, queued };

  // A7 / Q-481: keep every touched card of a refreshed kind; fill the remaining
  // slots of that kind with the newest untouched ones. Overflow remains active in
  // queuedActions. This only chooses a view and cannot race an officer write at admission.
  const slotsFor = (kind: RefreshKind, touched: number) => Math.max(0, kind === 'question'
    ? MAX_VISIBLE_QUESTIONS - touched
    : MAX_VISIBLE_NON_QUESTIONS - taskCount - touched);
  const shownByKind = new Map<RefreshKind, Set<string>>();
  const candidatesByKind = new Map<RefreshKind, CanonicalActionRow[]>();
  for (const kind of refreshed) {
    const cards = activeRows.filter((row) => normalizeActionType(row.action_type) === kind &&
      row.display_state !== 'hidden' && row.display_state !== 'archived' && !isRequiredSystemRow(row));
    const touched = cards.filter((row) => isOfficerTouchedSuggestion(row, speechQueue));
    const untouched = cards.filter((row) => !isOfficerTouchedSuggestion(row, speechQueue));
    const newest = [...untouched].sort((a, b) =>
      String(b.created_at || '').localeCompare(String(a.created_at || '')) || actionSort(a, b))
      .slice(0, slotsFor(kind, touched.length));
    shownByKind.set(kind, new Set([...touched, ...newest].map((row) => row.action_key)));
    candidatesByKind.set(kind, cards);
  }
  const kindOf = (row: CanonicalActionRow) => normalizeActionType(row.action_type) as RefreshKind;
  const isShown = (row: CanonicalActionRow) => shownByKind.has(kindOf(row)) && candidatesByKind.get(kindOf(row))!.includes(row)
    ? shownByKind.get(kindOf(row))!.has(row.action_key) : visible.includes(row);
  return {
    visible: activeRows.filter(isShown),
    queued: activeRows.filter((row) => !isShown(row) &&
      (queued.includes(row) || (shownByKind.has(kindOf(row)) && candidatesByKind.get(kindOf(row))!.includes(row)))),
  };
}

export function projectCanonicalActions(
  rows: CanonicalActionRow[],
  options: {
    speechQueue?: CanonicalSpeechQueueRow[];
    attempts?: CanonicalSpeechAttemptRow[];
    groups?: CanonicalActionGroupProjection[];
    refreshDisplayPolicy?: boolean;
  } = {},
): CanonicalProjection {
  const activeRows = rows.filter((row) => row.lifecycle_state !== 'purged').sort(actionSort);
  const attemptsByKey = new Map<string, CanonicalSpeechAttemptRow[]>();
  for (const attempt of options.attempts || []) {
    const existing = attemptsByKey.get(attempt.action_key) || [];
    existing.push(attempt);
    attemptsByKey.set(attempt.action_key, existing);
  }

  const refreshAdmitted = options.refreshDisplayPolicy === true &&
    REFRESH_KINDS.some((kind) => hasActiveRefreshOf(activeRows, kind));

  const actionsByKey = new Map<string, SuggestedAction>(
    activeRows.map((row): [string, SuggestedAction] => {
      const action = canonicalRowToLegacyAction(row, attemptsByKey.get(row.action_key));
      return [row.action_key, {
        ...action,
        displayTouched: isOfficerTouchedSuggestion(row, options.speechQueue),
        refreshDisplayPolicy: refreshAdmitted,
        createdAt: row.created_at ? Date.parse(row.created_at) : action.createdAt,
      }];
    }),
  );

  const { visible: visibleRows, queued: queuedRows } = splitCanonicalRowsByDisplay(activeRows, options.speechQueue, options.refreshDisplayPolicy === true);
  const visibleKeys = new Set(visibleRows.map((row) => row.action_key));
  for (const [key, action] of actionsByKey) {
    actionsByKey.set(key, { ...action, displaySelected: visibleKeys.has(key) });
  }

  const toActions = (inputRows: CanonicalActionRow[]) =>
    inputRows.map((row) => actionsByKey.get(row.action_key)).filter((item): item is SuggestedAction => Boolean(item));

  const resolvedRows = activeRows.filter(
    (row) => row.lifecycle_state === 'resolved' || row.answer_status === 'complete',
  );
  const discardedRows = activeRows.filter(
    (row) =>
      row.lifecycle_state === 'dismissed' ||
      row.lifecycle_state === 'discarded' ||
      row.lifecycle_state === 'withdrawn' ||
      row.lifecycle_state === 'removed' ||
      row.lifecycle_state === 'follow_up',
  );
  const removedRows = activeRows.filter(
    (row) => row.lifecycle_state === 'removed' || row.lifecycle_state === 'follow_up',
  );
  const partialRows = activeRows.filter((row) => row.answer_status === 'partial');

  const speechQueue = (options.speechQueue || [])
    .filter((item) => item.status === 'queued' || item.status === 'in_flight')
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((item) => {
      const action = actionsByKey.get(item.action_key);
      return {
        id: getLegacyActionId({ action_key: item.action_key, source_action_id: action?.id }),
        actionId: action?.id || item.action_key,
        text: item.action_text || action?.details || '',
        type: action?.type,
        priority: action?.priority,
        queueOrigin: item.queue_origin || 'auto',
        source: item.source || 'auto-response',
        status: item.status || 'queued',
        attemptId: item.attempt_id || undefined,
        claimedAt: item.claimed_at || undefined,
        addedAt: item.created_at || undefined,
        updatedAt: item.updated_at || undefined,
      };
    });

  const canonicalRevision = [
    ...activeRows.map(rowProjectionRevision),
    ...(options.speechQueue || []).map(speechQueueProjectionRevision),
    ...(options.attempts || []).map(speechAttemptProjectionRevision),
  ].reduce((max, revision) => Math.max(max, revision), 0);

  return {
    suggestedActions: toActions(visibleRows),
    queuedActions: toActions(queuedRows),
    allSuggestedActions: toActions(activeRows),
    resolvedSuggestions: toActions(resolvedRows.sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))),
    discardedSuggestions: toActions(discardedRows.sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))),
    removedSuggestions: toActions(removedRows.sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))),
    partialSuggestions: toActions(partialRows.sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')))),
    answeredPrompts: uniqStrings(resolvedRows.map(getLegacyActionId)),
    partialPrompts: uniqStrings(partialRows.map(getLegacyActionId)),
    actionGroups: options.groups || [],
    speechQueue,
    canonicalRevision,
  };
}

export function buildLegacyFullStateProjection(
  existingFullState: Partial<AiAnalysisState> | null | undefined,
  projection: CanonicalProjection,
): Partial<AiAnalysisState> {
  return {
    ...(existingFullState || {}),
    suggestedActions: projection.suggestedActions,
    queuedActions: projection.queuedActions,
    allSuggestedActions: projection.allSuggestedActions,
    resolvedSuggestions: projection.resolvedSuggestions,
    discardedSuggestions: projection.discardedSuggestions,
    removedSuggestions: projection.removedSuggestions,
    partialSuggestions: projection.partialSuggestions,
    answeredPrompts: projection.answeredPrompts,
    partialPrompts: projection.partialPrompts,
  };
}
